import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Filter, Globe, MapPin, Clock, DollarSign, X
} from 'lucide-react';

/**
 * Advanced filter panel (popover) for SmartSearch.
 * Extracted from SmartSearch.jsx to reduce component size.
 */
export function SearchFilterPanel({
  activeFilters,
  setActiveFilters,
  filterPackages,
  searchQuery,
  clearFilters,
  activeFilterCount,
}) {
  const updateFilter = (key, value) => {
    const newFilters = { ...activeFilters, [key]: value };
    setActiveFilters(newFilters);
    filterPackages(newFilters, searchQuery);
  };

  const toggleFilter = (key, value, emptyValue) => {
    updateFilter(key, activeFilters[key] === value ? emptyValue : value);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-14 px-6 rounded-full border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:text-white relative"
          data-testid="smart-search-filters"
        >
          <Filter className="h-5 w-5 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-6 w-6 p-0 flex items-center justify-center bg-coral">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <div className="space-y-4">
          <h4 className="font-semibold text-slate-900">Advanced Filters</h4>
          
          {/* Category */}
          <div>
            <label className="text-sm font-medium text-slate-700">Category</label>
            <div className="flex gap-2 mt-1">
              <Button
                variant={activeFilters.category === 'domestic' ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleFilter('category', 'domestic', '')}
              >
                🇮🇳 Domestic
              </Button>
              <Button
                variant={activeFilters.category === 'international' ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleFilter('category', 'international', '')}
              >
                ✈️ International
              </Button>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="text-sm font-medium text-slate-700">Duration</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {[3, 5, 7, 10, 14].map(d => (
                <Button
                  key={d}
                  variant={activeFilters.duration === d ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFilter('duration', d, null)}
                >
                  {d} Nights
                </Button>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div>
            <label className="text-sm font-medium text-slate-700">
              Budget: Up to ₹{activeFilters.priceRange[1].toLocaleString()}
            </label>
            <Slider
              value={[activeFilters.priceRange[1]]}
              max={500000}
              min={10000}
              step={10000}
              className="mt-2"
              onValueChange={(value) => {
                updateFilter('priceRange', [0, value[0]]);
              }}
            />
          </div>

          {/* Tour Type */}
          <div>
            <label className="text-sm font-medium text-slate-700">Tour Type</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {[
                { value: 'honeymoon', label: 'Honeymoon', icon: '💑' },
                { value: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦' },
                { value: 'group', label: 'Group', icon: '👥' },
                { value: 'solo', label: 'Solo', icon: '🎒' },
              ].map(t => (
                <Button
                  key={t.value}
                  variant={activeFilters.type === t.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFilter('type', t.value, '')}
                >
                  {t.icon} {t.label}
                </Button>
              ))}
            </div>
          </div>

          {activeFilterCount > 0 && (
            <Button variant="ghost" className="w-full" onClick={clearFilters}>
              Clear All Filters
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Active filter tag badges displayed below the search bar.
 * Extracted from SmartSearch.jsx to reduce component size.
 */
export function ActiveFilterTags({
  activeFilters,
  setActiveFilters,
  filterPackages,
  searchQuery,
}) {
  const removeFilter = (key, emptyValue) => {
    const newFilters = { ...activeFilters, [key]: emptyValue };
    setActiveFilters(newFilters);
    filterPackages(newFilters, searchQuery);
  };

  const tags = [];

  if (activeFilters.country) {
    tags.push(
      <Badge key="country" variant="secondary" className="px-3 py-1">
        <Globe className="h-3 w-3 mr-1" />
        {activeFilters.country}
        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeFilter('country', '')} />
      </Badge>
    );
  }
  if (activeFilters.destination) {
    tags.push(
      <Badge key="destination" variant="secondary" className="px-3 py-1">
        <MapPin className="h-3 w-3 mr-1" />
        {activeFilters.destination}
        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeFilter('destination', '')} />
      </Badge>
    );
  }
  if (activeFilters.category) {
    tags.push(
      <Badge key="category" variant="secondary" className="px-3 py-1">
        {activeFilters.category === 'domestic' ? '🇮🇳' : '✈️'} {activeFilters.category}
        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeFilter('category', '')} />
      </Badge>
    );
  }
  if (activeFilters.duration) {
    tags.push(
      <Badge key="duration" variant="secondary" className="px-3 py-1">
        <Clock className="h-3 w-3 mr-1" />
        {activeFilters.duration} Nights
        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeFilter('duration', null)} />
      </Badge>
    );
  }
  if (activeFilters.type) {
    tags.push(
      <Badge key="type" variant="secondary" className="px-3 py-1">
        {activeFilters.type}
        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeFilter('type', '')} />
      </Badge>
    );
  }
  if (activeFilters.priceRange[1] < 500000) {
    tags.push(
      <Badge key="price" variant="secondary" className="px-3 py-1">
        <DollarSign className="h-3 w-3 mr-1" />
        Under ₹{activeFilters.priceRange[1].toLocaleString()}
        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeFilter('priceRange', [0, 500000])} />
      </Badge>
    );
  }

  if (tags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {tags}
    </div>
  );
}
